import { FindHintSource } from '../components/document-viewer/locate/find-hints';

export interface SwProfile extends FindHintSource {
    fullName: string;
    employer: string;
    vendor: string;
    planet: string;
    city: string;
    ship: string;
    merchant: string;
    route: string;
    seed: number;
}

const FIRST = ['Luke', 'Leia', 'Han', 'Padme', 'Anakin', 'ObiWan', 'Ahsoka', 'Din', 'Rey', 'Finn', 'Poe', 'Jyn', 'Cassian', 'Mace', 'QuiGon'];
const LAST = ['Skywalker', 'Organa', 'Solo', 'Amidala', 'Kenobi', 'Tano', 'Djarin', 'Palpatine', 'Antilles', 'Erso', 'Andor', 'Windu', 'Jinn', 'Calrissian', 'Fett'];
const EMPLOYERS = ['Rebel Alliance Payroll', 'New Republic Civil Service', 'Jedi Order Archives', 'Resistance Logistics', 'Outer Rim Freight Co'];
const VENDORS = ['Mos Eisley Inn', 'Cloud City Lodging', 'Galaxy Tours', 'Coruscant Transit', 'Naboo Lakeside Hotel', 'Hoth Outpost Supply'];
const MERCHANTS = ['Dexter Jettster Diner', 'Maz Kanata Cantina', 'Lars Homestead Market', 'Toshi Station Fuel', 'Echo Base Mess'];
const PLANETS = ['Tatooine', 'Alderaan Sector', 'Naboo', 'Corellia', 'Lothal', 'Jedha'];
const CITIES = ['Anchorhead', 'Theed', 'Coruscant Mid-Level', 'Mos Espa', 'Niima Outpost'];
const SHIPS = ['Millennium Falcon', 'Ghost', 'Razor Crest', 'X-Wing Red Five', 'Slave I'];
const ROUTES = ['Tatooine to Alderaan', 'Naboo to Coruscant', 'Jakku to Takodana', 'Lothal to Atollon', 'Corellia to Scarif'];

function pick<T>(arr: T[], seed: number, salt: number): T {
    const i = Math.abs((seed * 31 + salt * 17) | 0) % arr.length;
    return arr[i];
}

function pad2(n: number): string {
    return n < 10 ? '0' + n : String(n);
}

function money(seed: number, salt: number, base: number): number {
    const cents = Math.abs((seed * 13 + salt * 7) % 9000) / 100;
    return Math.round((base + cents) * 100) / 100;
}

export function createSwProfile(seed: number = 42): SwProfile {
    const firstName = pick(FIRST, seed, 1);
    const lastName = pick(LAST, seed, 2);
    const fullName = firstName + ' ' + lastName;
    const ssnCore = String(100000000 + Math.abs((seed * 97) % 899999999));
    const year = 2020 + (Math.abs(seed) % 5);
    const stubGross = money(seed, 3, 2100);
    const irsGross = money(seed, 4, 52000);
    const receiptAmount = money(seed, 5, 120);
    const startDay = 1 + (Math.abs(seed) % 12);
    const endDay = startDay + 13;

    return {
        seed,
        fullName,
        firstName,
        lastName,
        name: fullName,
        alias: 'SW-' + pick(['RED', 'BLUE', 'GOLD', 'ROGUE'], seed, 6) + '-' + pad2((seed % 99) + 1),
        donorAlias: 'D-SW-' + pad2((seed % 99) + 1),
        ssnDecrypted: ssnCore,
        birthDate: `${1960 + (seed % 30)}-${pad2(1 + (seed % 12))}-${pad2(1 + (seed % 27))}`,
        incomeIrsYear: year,
        incomeIrsYearGross: irsGross,
        incomePayStubGross: stubGross,
        incomePayStubStartDate: `${year + 1}-01-${pad2(startDay)}`,
        incomePayStubEndDate: `${year + 1}-01-${pad2(Math.min(endDay, 28))}`,
        email: firstName.toLowerCase() + '.' + lastName.toLowerCase() + '@alliance.sw',
        receiptAmount,
        receiptDate: `${year + 1}-0${1 + (seed % 9)}-${pad2(1 + (seed % 27))}`,
        employer: pick(EMPLOYERS, seed, 8),
        vendor: pick(VENDORS, seed, 9),
        planet: pick(PLANETS, seed, 10),
        city: pick(CITIES, seed, 11),
        ship: pick(SHIPS, seed, 12),
        merchant: pick(MERCHANTS, seed, 13),
        route: pick(ROUTES, seed, 14)
    };
}

export function formatMoney(n: number): string {
    return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function formatMoneyPlain(n: number): string {
    return n.toFixed(2);
}

export function formatDate(raw: string): string {
    const m = (raw || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) {
        return `${m[2]}/${m[3]}/${m[1]}`;
    }
    return raw || '';
}
